import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';

/**
 * SellShareModal — post-sell celebration + share-to-social modal.
 *
 * Fires confetti on mount ONLY when the sell was profitable. Losses get a
 * dignified modal with no animation — the "Standard tuition fee paid. We
 * reload!" copy carries the moment without being condescending.
 * Renders the exact layout the user sketched:
 *   [Creator Name]
 *   [ +/-$ big number ]
 *   PNL %, Invested, Position
 *   [Hype copy text]
 *   [Share to X] [Share to IG] [Share to FB] [Close]
 *
 * Copy templates locked with user (Phase 7):
 *   PROFIT: "Just cashed out {N} Bits of @{creator} for +{X} pts on @clanster! 🚀 Up {PNL}% and shifting gears!"
 *   LOSS:   "Left {N} Bits of @{creator} behind for -{X} pts on @clanster 📉 Standard tuition fee paid. We reload!"
 */
export default function SellShareModal({
  open,
  onClose,
  creatorName,
  creatorHandle,
  qty,
  proceeds,           // pts user received after fee (positive number)
  invested,           // avg-cost basis of the sold Bits (pts)
  referralLink,       // full URL for the share
}) {
  const [copied, setCopied] = useState(false);
  const pnlPts = proceeds - invested;
  const isProfit = pnlPts >= 0;
  const pnlPct = invested > 0 ? (pnlPts / invested) * 100 : 0;

  useEffect(() => {
    if (!open) return;
    // Only celebrate on a profit — losses get a quiet, dignified modal (no
    // confetti, no falling emojis). Copy alone carries the beat.
    if (!isProfit) return;
    const colors = ['#B4FF00', '#00B4FF', '#FF5722', '#E040FB'];
    // Left + right cannons for a satisfying burst
    confetti({
      particleCount: 90, spread: 70, origin: { x: 0.15, y: 0.6 }, colors,
      scalar: 1.05,
    });
    confetti({
      particleCount: 90, spread: 70, origin: { x: 0.85, y: 0.6 }, colors,
      scalar: 1.05,
    });
    // extra rain on profits
    setTimeout(() => {
      confetti({ particleCount: 60, spread: 90, origin: { y: 0.2 }, colors });
    }, 220);
  }, [open, isProfit]);

  if (!open) return null;

  const pnlAbs = Math.abs(pnlPts).toFixed(2);
  const pctStr = `${Math.abs(pnlPct).toFixed(2)}%`;
  const shareText = isProfit
    ? `Just cashed out ${qty} Bit${qty === 1 ? '' : 's'} of @${creatorHandle} for +${pnlAbs} pts on @clanster! 🚀 Up ${pctStr} and shifting gears!`
    : `Left ${qty} Bit${qty === 1 ? '' : 's'} of @${creatorHandle} behind for -${pnlAbs} pts on @clanster 📉 Standard tuition fee paid. We reload!`;

  const shareUrl = referralLink;
  const fullText = `${shareText} ${shareUrl}`;

  const openWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=680,height=560');
  };
  const shareX = () => openWindow(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
  );
  const shareFB = () => openWindow(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
  );
  const shareIG = async () => {
    // Instagram doesn't support web share intents — copy to clipboard.
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <div className="ssm-scrim" onClick={onClose} data-testid="sell-share-scrim" />
      <div
        className="ssm-modal"
        role="dialog"
        aria-modal="true"
        data-testid="sell-share-modal"
      >
        <button
          className="ssm-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="sell-share-close"
        >
          <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="ssm-header">
          <div className="ssm-creator-name" data-testid="ssm-creator-name">
            {creatorName}
          </div>
          <div className="ssm-creator-handle">@{creatorHandle}</div>
        </div>

        <div className={`ssm-hero ${isProfit ? 'profit' : 'loss'}`} data-testid="ssm-hero">
          <div className="ssm-hero-label">{isProfit ? '🎉 Realized profit' : '😅 Realized loss'}</div>
          <div className="ssm-hero-value" data-testid="ssm-hero-value">
            {isProfit ? '+' : '−'}{pnlAbs} pts
          </div>
        </div>

        <div className="ssm-rows">
          <div className="ssm-row">
            <span className="ssm-row-label">PNL %</span>
            <span
              className={`ssm-row-value ${isProfit ? 'up' : 'down'}`}
              data-testid="ssm-pnl-pct"
            >
              {isProfit ? '+' : '−'}{pctStr}
            </span>
          </div>
          <div className="ssm-row">
            <span className="ssm-row-label">Invested (cost basis)</span>
            <span className="ssm-row-value" data-testid="ssm-invested">
              {invested.toFixed(2)} pts
            </span>
          </div>
          <div className="ssm-row">
            <span className="ssm-row-label">Proceeds ({qty} Bit{qty === 1 ? '' : 's'})</span>
            <span className="ssm-row-value" data-testid="ssm-proceeds">
              {proceeds.toFixed(2)} pts
            </span>
          </div>
        </div>

        <div className="ssm-copy-box" data-testid="ssm-copy-box">
          <div className="ssm-copy-label">Share it 👇</div>
          <div className="ssm-copy-text">{shareText}</div>
        </div>

        <div className="ssm-share-row">
          <button
            className="ssm-share-btn ssm-x"
            onClick={shareX}
            data-testid="ssm-share-x"
            aria-label="Share on X"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X
          </button>
          <button
            className="ssm-share-btn ssm-ig"
            onClick={shareIG}
            data-testid="ssm-share-ig"
            aria-label="Copy for Instagram"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="18" height="18">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
            {copied ? '✓ Copied' : 'IG'}
          </button>
          <button
            className="ssm-share-btn ssm-fb"
            onClick={shareFB}
            data-testid="ssm-share-fb"
            aria-label="Share on Facebook"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M13.5 21v-8h2.7l.4-3.2H13.5V7.7c0-.93.26-1.56 1.6-1.56H16.8V3.28A22.7 22.7 0 0 0 14.5 3.1c-2.31 0-3.89 1.41-3.89 4V9.8H8v3.2h2.6V21h2.9z" />
            </svg>
            FB
          </button>
        </div>

        <button
          className="ssm-primary-close"
          onClick={onClose}
          data-testid="ssm-continue"
        >
          Continue trading →
        </button>
      </div>
    </>
  );
}
